import React, { useMemo, useState } from 'react';
import { BuildingIcon, ChevronRightIcon, PlusIcon, UserIcon, UsersIcon } from 'lucide-react';
import { ProCustomer, ProJob } from '../../types/pro';
import { formatDate } from '../../utils/pro';
import { PageHeader } from '../../components/pro/PageHeader';
import { SearchField } from '../../components/pro/SearchField';
import { EmptyState } from '../../components/pro/EmptyState';
import { contactSearchText, contactsOf } from '../../utils/contacts';
interface ProCustomersProps {
  customers: ProCustomer[];
  jobs: ProJob[];
  onOpen: (customerId: string) => void;
  onNew: () => void;
}
type Filter = 'alle' | 'aktive' | 'privat' | 'firma';
export function ProCustomers({
  customers,
  jobs,
  onOpen,
  onNew
}: ProCustomersProps) {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('alle');
  const rows = useMemo(() => {
    const term = query.trim().toLowerCase();
    return customers.map(customer => {
      const own = jobs.filter(job => job.customerId === customer.id);
      const active = own.filter(job => !['faerdig', 'faktureret'].includes(job.status)).length;
      const lastActivity = own.map(job => job.start ?? '').filter(Boolean).sort().pop();
      return {
        customer,
        active,
        lastActivity: lastActivity || customer.createdDate
      };
    }).filter(({
      customer,
      active
    }) => {
      if (filter === 'aktive' && active === 0) return false;
      if (filter === 'privat' && customer.type !== 'privat') return false;
      if (filter === 'firma' && customer.type !== 'firma') return false;
      if (!term) return true;
      return [customer.name, customer.customerNo, contactSearchText(customer), customer.cvr, customer.address, customer.city, customer.phone, customer.email].filter(Boolean).join(' ').toLowerCase().includes(term);
    }).sort((a, b) => a.customer.name.localeCompare(b.customer.name, 'da'));
  }, [customers, jobs, query, filter]);
  const filters: {
    id: Filter;
    label: string;
  }[] = [{
    id: 'alle',
    label: 'Alle'
  }, {
    id: 'aktive',
    label: 'Med aktive opgaver'
  }, {
    id: 'privat',
    label: 'Privat'
  }, {
    id: 'firma',
    label: 'Firma'
  }];
  const companies = customers.filter(customer => customer.type === 'firma').length;
  return <div data-id="element-1227" data-mp="e6s57y0s9y0m">
      <PageHeader title="Kunder" subtitle={`${customers.length} kunder i kartoteket · ${companies} firmaer`} actions={<button onClick={onNew} className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700" data-id="element-1229" data-mp="sx7rzh8ule0w">
            <PlusIcon className="h-4 w-4" data-id="element-1230" data-mp="xu7y1s5p6gs0" />
            Ny kunde
          </button>} data-id="element-1228" data-mp="e57deg5ust6g" />

      <div className="mb-4 flex flex-wrap items-center gap-3" data-id="element-1231" data-mp="1giig9vz602gi">
        <SearchField value={query} onChange={setQuery} placeholder="Søg navn, adresse, telefon…" className="min-w-[220px] flex-1" data-id="element-1232" data-mp="1gjbgee3mbtpo" />
        <div role="group" aria-label="Filtrer kunder" className="flex rounded-lg border border-stone-200 bg-white p-1" data-id="element-1233" data-mp="17h29dy7k6ih1">
          {filters.map(option => <button key={option.id} onClick={() => setFilter(option.id)} aria-pressed={filter === option.id} className={`whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${filter === option.id ? 'bg-stone-900 text-white' : 'text-stone-500 hover:text-stone-900'}`} data-id="element-1234" data-mp="xr8y6ybbw5db">
              {option.label}
            </button>)}
        </div>
      </div>

      {rows.length === 0 ? <EmptyState icon={UsersIcon} title="Ingen kunder matcher" description="Prøv en anden søgning eller et andet filter." data-id="element-1235" data-mp="1m5nv53dqv75q" /> : <>
          {/* Desktop table — density matters when scanning many customers */}
          <div className="hidden overflow-hidden rounded-xl border border-stone-200 bg-white md:block" data-id="element-1236" data-mp="1dbj16b3bdflz">
            <table className="w-full text-sm" data-id="element-1237" data-mp="18c6dxp8j66iv">
              <thead data-id="element-1238" data-mp="13ffyn44jveyi">
                <tr className="border-b border-stone-200 bg-stone-50 text-left" data-id="element-1239" data-mp="13fz3pk93q8v4">
                  {['Kunde', 'Adresse', 'Telefon', 'E-mail', 'Aktive opgaver', 'Seneste aktivitet'].map(heading => <th key={heading} scope="col" className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-stone-500" data-id="element-1240" data-mp="phr9vl7d3no6">
                      {heading}
                    </th>)}
                  <th scope="col" className="w-10" data-id="element-1241" data-mp="2wc1raj3kds8n" />
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100" data-id="element-1242" data-mp="2w9p1jjrkedl1">
                {rows.map(({
              customer,
              active,
              lastActivity
            }) => <tr key={customer.id} onClick={() => customer.id && onOpen(customer.id)} className="cursor-pointer transition-colors hover:bg-stone-50" data-id="element-1243" data-mp="362cu22bihvdy">
                    <td className="px-4 py-3" data-id="element-1244" data-mp="2ddpfsqp6k69w">
                      <span className="flex items-center gap-2" data-id="element-1245" data-mp="2icikbtmhomxx">
                        {customer.type === 'firma' ? <BuildingIcon className="h-3.5 w-3.5 flex-shrink-0 text-stone-400" aria-label="Firma" data-id="element-1246" data-mp="2me41vs9yxpeg" /> : <UserIcon className="h-3.5 w-3.5 flex-shrink-0 text-stone-300" aria-label="Privatperson" data-id="element-1247" data-mp="1m1xoh16gqyo" />}
                        <span className="font-medium text-stone-900" data-id="element-1248" data-mp="1tragizine54k">
                          {customer.name}
                        </span>
                      </span>
                      <span className="block pl-[22px] text-xs text-stone-400" data-id="element-1249" data-mp="22qdx9wuce8fw">
                        {customer.customerNo}
                        {(() => {
                    const people = contactsOf(customer);
                    const lead = people.find(item => item.primary);
                    if (!lead?.name || lead.name === customer.name) {
                      return null;
                    }
                    return people.length > 1 ? ` · ${lead.name} +${people.length - 1}` : ` · ${lead.name}`;
                  })()}
                        {customer.cvr && ` · CVR ${customer.cvr}`}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-stone-600" data-id="element-1250" data-mp="37patj47nw2rc">
                      {customer.address}
                      <span className="block text-xs text-stone-400" data-id="element-1251" data-mp="2txw86qqa7ph4">
                        {customer.postalCode} {customer.city}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-stone-600" data-id="element-1252" data-mp="3mmbbwh3yqld5">
                      {customer.phone}
                    </td>
                    <td className="max-w-[200px] truncate px-4 py-3 text-stone-600" data-id="element-1253" data-mp="3qmc3l3g2ot25">
                      {customer.email}
                    </td>
                    <td className="px-4 py-3" data-id="element-1254" data-mp="3hmyo9u59pa9i">
                      {active > 0 ? <span className="inline-flex rounded-full bg-brand-50 px-2 py-0.5 text-xs font-semibold text-brand-700" data-id="element-1255" data-mp="3horytkj04ems">
                          {active}
                        </span> : <span className="text-stone-300" data-id="element-1256" data-mp="2xvwa6mo2s0jn">—</span>}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-stone-500" data-id="element-1257" data-mp="2sw0gphd07ric">
                      {formatDate(lastActivity)}
                    </td>
                    <td className="px-2" data-id="element-1258" data-mp="2nz9tma2s7vnf">
                      <ChevronRightIcon className="h-4 w-4 text-stone-300" data-id="element-1259" data-mp="2iyvtrfbc3s0h" />
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          {/* Phone — rows become compact cards */}
          <ul className="space-y-2 md:hidden" data-id="element-1260" data-mp="3boumbkoqm79w">
            {rows.map(({
          customer,
          active
        }) => <li key={customer.id} data-id="element-1261" data-mp="36tyilna51vgy">
                <button onClick={() => customer.id && onOpen(customer.id)} className="flex w-full items-center gap-3 rounded-xl border border-stone-200 bg-white p-4 text-left" data-id="element-1262" data-mp="31xhu2rz11f3c">
                  <span className="min-w-0 flex-1" data-id="element-1263" data-mp="3uku7abjasajz">
                    <span className="block truncate font-medium text-stone-900" data-id="element-1264" data-mp="6ovptht331rs">
                      {customer.name}
                    </span>
                    <span className="block truncate text-xs text-stone-500" data-id="element-1265" data-mp="ar8tm0o9splx">
                      {customer.address}, {customer.city}
                    </span>
                    <span className="mt-1 block text-xs text-stone-400" data-id="element-1266" data-mp="koddftun88zk">
                      {customer.phone}
                    </span>
                  </span>
                  {active > 0 && <span className="flex-shrink-0 rounded-full bg-brand-50 px-2 py-0.5 text-xs font-semibold text-brand-700" data-id="element-1267" data-mp="tp36ipsirl9l">
                      {active} aktive
                    </span>}
                  <ChevronRightIcon className="h-4 w-4 flex-shrink-0 text-stone-300" data-id="element-1268" data-mp="13fedkl7i2akp" />
                </button>
              </li>)}
          </ul>
        </>}
    </div>;
}